import { MeetingProvider, useAuthContext } from "@kalyzee/kast-web-sdk";
import { ROLE } from "@kalyzee/kast-web-sdk/http";
import { useURL } from "@kalyzee/kast-web-sdk/web-components";
import React, { useEffect } from "react";
import { Route } from "react-router-dom";
import { JoinPage } from "../pages/master/student/join/join";
import { AppNavigate, ListVodRedirect, LoginRedirect, NavigateSlave, PublicMeetingRedirect, PublicRedirect, useAuthRedirects } from "../helpers/navigation";
import { generateRoute } from "../components/navigation/Page";
import { PAGE_PATHS, PageMasterMode, PageSlaveMode } from "../components/navigation/PageContext";
import PrivateRoute from "../components/navigation/PrivateRoute";
import PublicRoute from "../components/navigation/PublicRoute";
import MasterRouteContainer from "../components/navigation/routes/MasterRouteContainer";
import SlaveRouteContainer from "../components/navigation/routes/SlaveRouteContainer";
import ConnexionView from "../components/utils/ConnexionView";
import { useAppStoreContext } from "../contexts/appStore/context";
import { SessionMode, StoreUtilsHooks } from "../contexts/appStore/hooks";
import { useAppStoreSelector } from "../contexts/appStore/selectors";
import { useLogger } from "../helpers/logger";
import { appMessage } from "../helpers/message";
import { getGlobalQuery, getGlobalQueryLabel, NavigateGlobalQuery } from "../helpers/navigation";
import { getStoredSettings, Settings } from "../helpers/settings";
import { setToken, TokenType } from "../helpers/storage";
import { meetingsContext } from "../hooks/meeting";
import { useSettings } from "../hooks/settings";
import { MeetingListStudentPage } from "../pages/master/student/list/list";
import GuestFallbackRoute from "../components/navigation/GuestFallback";
import { MeetingDashboardStudentPage } from "../pages/master/student/meeting/connected/dashboard";
import ForbiddenPage from "../pages/public/forbidden/forbidden";
import LoginPage from "../pages/public/login/login";
import LogoutPage from "../pages/public/logout/logout";
import MeetingSlavePage from "../pages/slave/meeting/meeting";

import { AppLoadingView } from "../components/utils/AppLoadingView";
import { LoadingView } from "../components/utils/LoadingView";
import { useSdkContext } from "../contexts/sdk/context";
import styles from "./App.module.css";
import { MeetingInitPage } from "../pages/master/student/meeting/init";
import { MeetingLobbyPage } from "../pages/master/student/lobby/lobby";
import { MeetingPlayerPage } from "../pages/master/student/player/player";

function App() {
  const url = useURL();
  const store = useAppStoreContext();

  const mode = useAppStoreSelector(state => state.mode);
  const user = useAppStoreSelector(state => state.user);
  const userFetchStatus = useAppStoreSelector(state => state.userFetchStatus);

  const fetchUser = StoreUtilsHooks.useFetchUser(store);

  const [settings, setSettings] = useSettings();
  const logger = useLogger();
  const { status: authStatus } = useAuthContext();

  const sdkCtx = useSdkContext();

  useAuthRedirects();

  useEffect(() => {
    if (getGlobalQuery(url.href, NavigateGlobalQuery.SLAVE) && mode !== SessionMode.SLAVE) {
      store.dispatch("setMode", SessionMode.SLAVE);
    } else if (!mode) store.dispatch("setMode", SessionMode.MASTER);
    const screenIndex = getGlobalQuery(url.href, NavigateGlobalQuery.SCREEN_INDEX);
    const screenName = getGlobalQuery(url.href, NavigateGlobalQuery.SCREEN_NAME);
    const screenLayoutId = getGlobalQuery(url.href, NavigateGlobalQuery.SCREEN_LAYOUT_ID);
    if (screenIndex !== undefined || screenName || screenLayoutId !== undefined) {
      store.dispatch("setScreen", {
        index: screenIndex,
        name: screenName,
        layoutId: screenLayoutId,
      });
    }
    const disableGoogleLogin = getGlobalQuery(url.href, NavigateGlobalQuery.DISABLE_GOOGLE_LOGIN);
    if (disableGoogleLogin !== undefined) store.dispatch("setDisableGoogleLogin", disableGoogleLogin);
  }, [url, mode]);

  useEffect(() => {
    const microphoneListener = (mute: boolean) => {
      store.dispatch("setMicrophone", mute);
    };
    appMessage.addEventListener("microphone", microphoneListener);
    return () => {
      appMessage.removeEventListener("microphone", microphoneListener);
    };
  }, []);

  useEffect(() => {
    appMessage.getMicrohoneData();
    navigator.mediaDevices?.enumerateDevices().then((data) => logger.log("devices : ", data));
    return () => {
      appMessage.close();
    };
  }, []);

  useEffect(() => {
    appMessage.ready(settings.secret?.remotePassword ?? undefined);
  }, [settings.secret?.remotePassword]);

  useEffect(() => {
    // The bridge exchanges the raw stored layer: a host reading then writing
    // settings back must not bake in transient env/URL values.
    const getListener = () => appMessage.postSettings(getStoredSettings());
    const setListener = (data: Partial<Settings>) => {
      // save already broadcasts via appMessage.postSettings
      const newSettings = setSettings(data, { save: true });
      logger.log("localstorageSetSettings : ", newSettings);
    };
    appMessage.addEventListener("localstorageGetSettings", getListener);
    appMessage.addEventListener("localstorageSetSettings", setListener);
    return () => {
      appMessage.removeEventListener("localstorageGetSettings", getListener);
      appMessage.removeEventListener("localstorageSetSettings", setListener);
    };
  }, [setSettings]);

  const provideMeetingContext = (children: React.ReactNode) =>  (
    <MeetingProvider
      context={meetingsContext}
      sdkClient={sdkCtx.client}
      useChatApdapter
      userId={user?.id}
      incomingStreamSessionValidator={(meeting, mySession, incomingSession) => {
        return incomingSession.id !== mySession.id;
      }}
      logs={settings.logs?.meetingContext}
      options={{
        receptionManager: {
          ovenMediaSession: {
            options: {
              retryAfter: 500,
              video: { mode: "subscription", delayToDisableStream: 5000 },
              audio: true,
            },
          },
          logger: settings.logs?.ovenMediaSession?.reception?.main ? logger.log : undefined,
          webrtcLogger: settings.logs?.ovenMediaSession?.reception?.webrtc ? logger.log : undefined,
        },
        emissionManager: {
          sdp: {
            formatter: {
              offer: settings?.emission?.sdpFormatter?.offer,
              answer: settings?.emission?.sdpFormatter?.answer,
            },
          },
          logger: settings.logs?.ovenMediaSession?.emission?.main ? logger.log : undefined,
          webrtcLogger: settings.logs?.ovenMediaSession?.emission?.webrtc ? logger.log : undefined,
        },
      }}
    >
      {children}
    </MeetingProvider>
  );

  const privateRoutes = () => {
    if (!mode) return <LoadingView fullscreen />;

    if (mode === SessionMode.SLAVE) {
      return (
        <SlaveRouteContainer element={<PrivateRoute mode={mode} authStatus={authStatus} />}>
          {generateRoute(SessionMode.SLAVE, PageSlaveMode.MEETING, <MeetingSlavePage />)}
          <Route path="" element={<NavigateSlave to={PAGE_PATHS[mode][PageSlaveMode.MEETING]} />} />
          <Route path="*" element={<NavigateSlave to={PAGE_PATHS[mode][PageSlaveMode.MEETING]} />} />
        </SlaveRouteContainer>
      );
    }

    if (!user) return <AppLoadingView />;

    // Guests have no home page: their session is bound to a meeting, so the
    // fallback routes send them back to their lobby (or end the session).
    const isGuest = user.role === ROLE.GUEST;
    const homePath = isGuest ? "/" : PAGE_PATHS[mode][PageMasterMode.LIST];
    const fallbackRoute = isGuest ? <GuestFallbackRoute /> : <AppNavigate to={homePath} />;

    return (
      <MasterRouteContainer element={<PrivateRoute mode={mode} authStatus={authStatus} />}>
        {generateRoute(SessionMode.MASTER, PageMasterMode.LOGOUT, <LogoutPage />)}
        {generateRoute(SessionMode.MASTER, PageMasterMode.LOGIN, <LoginRedirect defaultTo={homePath} />)}
        {!isGuest && generateRoute(SessionMode.MASTER, PageMasterMode.LIST, <MeetingListStudentPage />)}
        {/* Guests keep no list page, but a /list?vodId= replay link must not
            end their session: play it on /player (registered in this tree)
            instead of falling into the logout catch-all. */}
        {isGuest && <Route path={PAGE_PATHS[SessionMode.MASTER][PageMasterMode.LIST]} element={<ListVodRedirect fallback={<GuestFallbackRoute />} />} />}
        {generateRoute(SessionMode.MASTER, PageMasterMode.JOIN, <JoinPage />)}
        {generateRoute(SessionMode.MASTER, PageMasterMode.LOBBY, <MeetingLobbyPage />)}
        {generateRoute(SessionMode.MASTER, PageMasterMode.PLAYER, <MeetingPlayerPage />)}
        {generateRoute(SessionMode.MASTER, PageMasterMode.MEETING, provideMeetingContext(<MeetingInitPage />), {
          children: (
            <>
              {generateRoute(SessionMode.MASTER, PageMasterMode.MEETING_DASHBOARD, <MeetingDashboardStudentPage />)}
            </>
          ),
        })}
        <Route path="" element={fallbackRoute} />
        <Route path="*" element={fallbackRoute} />
      </MasterRouteContainer>
    );
  };

  const publicRoutes = () => {
    if (!mode) return <LoadingView fullscreen />;
    if (mode === SessionMode.SLAVE || getGlobalQuery(url.href, NavigateGlobalQuery.SLAVE)) {
      return (
        <SlaveRouteContainer element={<PublicRoute mode={mode} authStatus={authStatus} />}>
          <Route path="" element={<PublicRedirect mode={mode} />} />
          <Route path="*" element={<PublicRedirect mode={mode} />} />
        </SlaveRouteContainer>
      );
    }
    return (
      <MasterRouteContainer element={<PublicRoute mode={mode} authStatus={authStatus} />}>
        {generateRoute(SessionMode.MASTER, PageMasterMode.LOGIN, <LoginPage />)}
        {generateRoute(SessionMode.MASTER, PageMasterMode.FORBIDDEN, <ForbiddenPage />)}
        {generateRoute(SessionMode.MASTER, PageMasterMode.JOIN, <JoinPage />)}
        {generateRoute(SessionMode.MASTER, PageMasterMode.LOBBY, <MeetingLobbyPage />)}
        {generateRoute(SessionMode.MASTER, PageMasterMode.PLAYER, <MeetingPlayerPage />)}
        {/* In-meeting URLs resolve to the public lobby (pre-join gate) — e.g. a
            guest reloading mid-meeting, whose ephemeral session ended. */}
        <Route path={`${PAGE_PATHS[SessionMode.MASTER][PageMasterMode.MEETING]}/*`} element={<PublicMeetingRedirect />} />
        {/* /list?vodId=<id> deep-links play on the public /player route;
            otherwise the usual logged-out handling (login with a redirect
            back to /list). */}
        <Route path={PAGE_PATHS[SessionMode.MASTER][PageMasterMode.LIST]} element={<ListVodRedirect fallback={<PublicRedirect mode={mode} />} />} />
        <Route path="" element={<PublicRedirect mode={mode} />} />
        <Route path="*" element={<PublicRedirect mode={mode} />} />
      </MasterRouteContainer>
    );
  };

  const routes = () => {
    if (authStatus === "in") {
      if (user) return privateRoutes();
      return (
        <ConnexionView
          status={userFetchStatus}
          retry={() => {
            if (!user) {
              fetchUser();
              return;
            }
          }}
        />
      );
    }
    return publicRoutes();
  };

  return <div className={styles.background}>{routes()}</div>;
}

export default App;
